import { v4 } from 'uuid';

import { api, assertNever } from 'common';

export function getNewCondition(): api.FlowDefinition.Conditional.ConditionalCondition {
  return {
    id: v4(),
    // We are initializing inputValue field with the empty string, even though it will not validate
    inputValue: '',
    dataType: api.FlowDefinition.Conditional.ConditionalFieldDataType.String,
    operation: api.FlowDefinition.Conditional.ConditionalOperation.Equals,
    caseSensitivity: api.FlowDefinition.Conditional.ConditionalCaseSensitivity.ExactMatch,
    value: '',
  };
}

export const DATA_TYPE_OPTIONS = [
  {
    label: 'Boolean',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.Boolean,
  },
  {
    label: 'String',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.String,
  },
  {
    label: 'Date',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.Date,
  },
  {
    label: 'Date & Time',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.DateTime,
  },
  {
    label: 'Number',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.Number,
  },
  {
    label: 'Value',
    value: api.FlowDefinition.Conditional.ConditionalFieldDataType.Value,
  },
];

const OPERATION_LABEL_BY_OPERATION: {
  [
    Condition in api.FlowDefinition.Conditional.ConditionalCondition as Condition['dataType']
  ]: Record<Condition['operation'], string>;
} = {
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.Number]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.Equals]: 'equals',
    [api.FlowDefinition.Conditional.ConditionalOperation.NotEquals]: 'does not equal',
    [api.FlowDefinition.Conditional.ConditionalOperation.InRange]: 'is in range',
    [api.FlowDefinition.Conditional.ConditionalOperation.NotInRange]: 'is not in range',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThan]: 'is greater than',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThanOrEqualTo]:
      'is greater than or equal to',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThan]: 'is less than',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThanOrEqualTo]:
      'is less than or equal to',
  },
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.String]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.Equals]: 'equals',
    [api.FlowDefinition.Conditional.ConditionalOperation.NotEquals]: 'does not equal',
    [api.FlowDefinition.Conditional.ConditionalOperation.Contains]: 'contains',
    [api.FlowDefinition.Conditional.ConditionalOperation.DoesNotContain]: 'does not contain',
    [api.FlowDefinition.Conditional.ConditionalOperation.StartsWith]: 'starts with',
    [api.FlowDefinition.Conditional.ConditionalOperation.DoesNotStartWith]: 'does not start with',
    [api.FlowDefinition.Conditional.ConditionalOperation.EndsWith]: 'ends with',
    [api.FlowDefinition.Conditional.ConditionalOperation.DoesNotEndWith]: 'does not end with',
    [api.FlowDefinition.Conditional.ConditionalOperation.IsOneOf]: 'is one of',
    [api.FlowDefinition.Conditional.ConditionalOperation.IsNotOneOf]: 'is not one of',
  },
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.Date]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.Equals]: 'equals',
    [api.FlowDefinition.Conditional.ConditionalOperation.NotEquals]: 'does not equal',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThan]: 'is after',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThanOrEqualTo]:
      'is after or same day as',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThan]: 'is before',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThanOrEqualTo]:
      'is before or same day as',
  },
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.DateTime]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.Equals]: 'exactly same time as',
    [api.FlowDefinition.Conditional.ConditionalOperation.NotEquals]: 'not exactly same time as',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThan]: 'is after',
    [api.FlowDefinition.Conditional.ConditionalOperation.GreaterThanOrEqualTo]:
      'is after or exactly same time as',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThan]: 'is before',
    [api.FlowDefinition.Conditional.ConditionalOperation.LessThanOrEqualTo]:
      'is before or exactly same time as',
  },
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.Boolean]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.IsTrue]: 'is true',
    [api.FlowDefinition.Conditional.ConditionalOperation.IsFalse]: 'is false',
  },
  [api.FlowDefinition.Conditional.ConditionalFieldDataType.Value]: {
    [api.FlowDefinition.Conditional.ConditionalOperation.Exists]: 'exists',
    [api.FlowDefinition.Conditional.ConditionalOperation.DoesNotExist]: 'does not exist',
  },
};

export function getOperationLabel(condition: api.FlowDefinition.Conditional.ConditionalCondition) {
  const { dataType, operation } = condition;
  // these type narrowings are necessary to avoid type errors
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.Boolean) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.String) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.Number) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.Date) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.DateTime) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.Value) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  assertNever('Unsupported data type', dataType);
}

export const CASE_SENSITIVITY_OPTIONS = [
  {
    label: 'Exact Match',
    value: api.FlowDefinition.Conditional.ConditionalCaseSensitivity.ExactMatch,
  },
  {
    label: 'Ignore Case',
    value: api.FlowDefinition.Conditional.ConditionalCaseSensitivity.IgnoreCase,
  },
];
